import React, { useId } from 'react';
import MuiModal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { styled } from '@mui/material/styles';
import { dsV2 } from '../../ds-v2-tokens';

/**
 * Popin centrée du Design System V2 (TEC-3051) — valeurs exactes de la maquette (`.modal`).
 *
 * Overlay `rgba(33,33,33,.34)` + flou, fermeture Échap/clic extérieur (Modal MUI), piège de
 * focus natif, `role="dialog"` + `aria-modal`. En-tête (pastille d'icône bleue + titre Instrument
 * Serif 20px + croix 30px), corps défilant (padding 20, max-height 90vh), pied collant aligné à
 * droite. Pendant du `DrawerV2` pour les écrans appelés depuis « Actions mission ».
 */
export type ModalV2IconTone = 'bleu' | 'vert' | 'rouge' | 'amber' | 'violet';

export type ModalV2Props = {
  open: boolean;
  onClose: () => void;
  title: React.ReactNode;
  icon?: React.ReactNode;
  iconTone?: ModalV2IconTone;
  children: React.ReactNode;
  footer?: React.ReactNode;
  /** Largeur du panneau (desktop). Défaut 560px. */
  width?: number;
};

const Overlay = styled(Box)({
  position: 'fixed',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 24,
  outline: 'none',
});

const Panel = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  maxHeight: '90vh',
  backgroundColor: dsV2.color.blanc,
  borderRadius: dsV2.radius.lg,
  boxShadow: dsV2.shadow.lg,
  outline: 'none',
  overflow: 'hidden',
  textAlign: 'left',
});

const Headerbar = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
  padding: '16px 20px',
  borderBottom: `1px solid ${dsV2.color.border}`,
  flexShrink: 0,
});

const Content = styled(Box)({ padding: 20, overflowY: 'auto' });

const Footerbar = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: 10,
  padding: '14px 20px',
  borderTop: `1px solid ${dsV2.color.border}`,
  flexShrink: 0,
});

const ICON_TONE: Record<ModalV2IconTone, { bg: string; fg: string }> = {
  bleu: { bg: dsV2.color.bleuDim, fg: dsV2.color.bleu },
  vert: { bg: dsV2.color.vertDim, fg: dsV2.color.vert },
  rouge: { bg: dsV2.color.rougeDim, fg: dsV2.color.rouge },
  amber: { bg: dsV2.color.amberDim, fg: dsV2.color.amber },
  violet: { bg: dsV2.color.violetDim, fg: dsV2.color.violet },
};

const ModalV2 = ({ open, onClose, title, icon, iconTone = 'bleu', children, footer, width = 560 }: ModalV2Props) => {
  const titleId = useId();
  const tint = ICON_TONE[iconTone];
  return (
    <MuiModal
      open={open}
      onClose={onClose}
      aria-labelledby={titleId}
      slotProps={{ backdrop: { sx: { backgroundColor: 'rgba(33,33,33,0.34)', backdropFilter: 'blur(2px)' } } }}
    >
      <Overlay onClick={onClose}>
        <Panel
          role="dialog"
          aria-modal
          aria-labelledby={titleId}
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
          sx={{ width: `min(${width}px, 95vw)` }}
        >
          <Headerbar>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
              {icon ? (
                <Box
                  sx={{
                    width: 30,
                    height: 30,
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    backgroundColor: tint.bg,
                    color: tint.fg,
                  }}
                >
                  {icon}
                </Box>
              ) : null}
              <Box
                id={titleId}
                sx={{
                  fontFamily: dsV2.font.serif,
                  fontSize: 20,
                  letterSpacing: '-0.01em',
                  color: dsV2.color.noir,
                  lineHeight: 1.15,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {title}
              </Box>
            </Box>
            <IconButton
              aria-label="Fermer"
              onClick={onClose}
              disableRipple
              sx={{
                width: 30,
                height: 30,
                borderRadius: '7px',
                backgroundColor: dsV2.color.surface2,
                border: `1px solid ${dsV2.color.border}`,
                color: dsV2.color.t3,
                flexShrink: 0,
                '&:hover': {
                  backgroundColor: dsV2.color.rougeDim,
                  color: dsV2.color.rouge,
                  borderColor: dsV2.color.rougeBrd,
                },
              }}
            >
              <CloseRoundedIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Headerbar>
          <Content>{open ? children : null}</Content>
          {footer ? <Footerbar>{footer}</Footerbar> : null}
        </Panel>
      </Overlay>
    </MuiModal>
  );
};

export default ModalV2;
export { ModalV2 };
